/**
 * TemplateCard — premium animated card for a character template on the create page.
 *
 * Visual: per-template gradient background with an animated shimmer, a glowing
 * thematic icon centered, glassmorphism overlay with name + description + tag chips,
 * and an interactive "Use" hover state. Each template feels curated, not generic.
 */
import { useId } from 'react';
import {
  Heart, Skull, Sword, Flame, HeartCrack, Coffee, Gamepad2, Moon, Wine, Scroll, Rocket,
  Loader2, Sparkles, BookOpen,
} from 'lucide-react';

const ICONS = {
  Heart, Skull, Sword, Flame, HeartCrack, Coffee, Gamepad2, Moon, Wine, Scroll, Rocket, Sparkles, BookOpen,
};

const FALLBACK_VIBE = {
  icon: 'Sparkles',
  from: '#1B0E2E',
  via: '#7C3AED',
  to: '#0A0510',
  accent: '#C4B5FD',
};

export default function TemplateCard({ template, loading, onSelect }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const vibe = { ...FALLBACK_VIBE, ...(template.vibe || {}) };
  const Icon = ICONS[vibe.icon] || Sparkles;

  return (
    <button
      type="button"
      onClick={() => onSelect(template.id)}
      disabled={loading}
      className="group relative overflow-hidden rounded-2xl text-left aspect-[3/4] border border-white/[0.08] hover:border-white/20 active:scale-[0.98] transition-all duration-200 disabled:opacity-60 disabled:cursor-wait"
      data-testid={`template-${template.id}`}
      style={{
        background: `linear-gradient(160deg, ${vibe.from} 0%, ${vibe.via} 55%, ${vibe.to} 100%)`,
        boxShadow: `0 14px 36px -18px ${vibe.accent}66, inset 0 0 60px ${vibe.via}33`,
      }}
    >
      {/* Animated shimmer gradient */}
      <style>{`
        @keyframes tpl-shimmer-${uid} {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes tpl-float-${uid} {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-6px) rotate(-2deg); }
        }
        @keyframes tpl-pulse-${uid} {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50%      { opacity: 0.55; transform: scale(1.15); }
        }
        .tpl-shimmer-${uid} {
          animation: tpl-shimmer-${uid} 3.5s ease-in-out infinite;
        }
        .tpl-icon-${uid} {
          animation: tpl-float-${uid} 4.5s ease-in-out infinite;
        }
        .tpl-glow-${uid} {
          animation: tpl-pulse-${uid} 3s ease-in-out infinite;
        }
      `}</style>

      {/* Subtle noise/grain via SVG */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.07] mix-blend-overlay" aria-hidden="true">
        <filter id={`noise-${uid}`}>
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" />
          <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1 0" />
        </filter>
        <rect width="100%" height="100%" filter={`url(#noise-${uid})`} />
      </svg>

      {/* Animated shimmer band */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`tpl-shimmer-${uid} absolute -inset-y-4 w-1/3 -skew-x-12`}
          style={{
            background: `linear-gradient(90deg, transparent, ${vibe.accent}30, transparent)`,
          }}
        />
      </div>

      {/* Pulsing glow behind icon */}
      <div
        className={`tpl-glow-${uid} absolute top-[28%] left-1/2 -translate-x-1/2 w-24 h-24 rounded-full blur-2xl`}
        style={{ background: vibe.accent }}
      />

      {/* Centered floating icon */}
      <div className="absolute top-[22%] left-1/2 -translate-x-1/2">
        <div
          className={`tpl-icon-${uid} w-20 h-20 rounded-2xl flex items-center justify-center backdrop-blur-md border`}
          style={{
            background: `${vibe.accent}15`,
            borderColor: `${vibe.accent}50`,
            boxShadow: `0 8px 20px -6px ${vibe.accent}50, inset 0 0 20px ${vibe.accent}20`,
          }}
        >
          <Icon className="w-9 h-9" style={{ color: vibe.accent, filter: `drop-shadow(0 0 8px ${vibe.accent}80)` }} />
        </div>
      </div>

      {/* Bottom content overlay (glassmorphism) */}
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 via-black/60 to-transparent">
        <p className="text-[10px] uppercase tracking-[0.15em] font-bold mb-0.5" style={{ color: vibe.accent, opacity: 0.85 }}>
          {template.category}
        </p>
        <p className="text-sm font-bold text-white font-['Unbounded'] leading-tight line-clamp-1" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
          {template.name}
        </p>
        <p className="text-[11px] text-white/70 line-clamp-2 mt-1 leading-snug">
          {template.description}
        </p>

        {/* Tag chips */}
        {template.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {template.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="px-1.5 py-0.5 text-[9px] font-medium rounded-full border backdrop-blur-sm"
                style={{
                  background: `${vibe.accent}1A`,
                  borderColor: `${vibe.accent}40`,
                  color: vibe.accent,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* "Use" badge on hover */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">
        <span
          className="px-2 py-0.5 text-[10px] font-bold rounded-full backdrop-blur-md border"
          style={{ background: `${vibe.accent}25`, borderColor: `${vibe.accent}60`, color: '#fff' }}
        >
          Use →
        </span>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex items-center gap-2 text-white text-xs font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        </div>
      )}
    </button>
  );
}
